/**
 * Contact Page - Google Maps Implementation
 * Handles hospital map display, filtering, and interactions
 */

export default class ContactGoogleMaps {
  constructor() {
    // Google Maps API Key from WordPress theme options
    this.GOOGLE_MAPS_API_KEY = HM_Variables.google_maps_api_key || "";

    // Map instance
    this.map = null;

    // Store all markers
    this.markers = [];

    // Store all hospital data
    this.hospitals = [];

    // Active info window (only one open at a time)
    this.activeInfoWindow = null;

    // Filter state
    this.filterState = {
      currentBrandId: "all",
      searchKeyword: "",
    };

    // Vietnam center coordinates
    this.vietnamCenter = { lat: 16.2, lng: 107.0 };

    // Brand colors for markers
    this.brandColors = {
      hm: "#006991", // Teal
      hmg: "#C5A572", // Gold
      hp: "#4A90E2", // HP Blue
      tm: "#E91E63", // TM Pink
    };

    this.init();
  }

  /**
   * Initialize the contact page functionality
   */
  init() {
    if (document.querySelector("#hospital-map")) {
      this.loadGoogleMapsScript();
    }

    if (document.querySelector("#the-contact-form")) {
      this.initContactForm();
    }
  }

  /**
   * Load Google Maps JavaScript API
   */
  loadGoogleMapsScript() {
    // Check if API key is provided
    if (!this.GOOGLE_MAPS_API_KEY || this.GOOGLE_MAPS_API_KEY.trim() === "") {
      console.error(
        "❌ Google Maps API key is not configured. Please add it in Theme Settings > API Setting > Google Maps API Key"
      );
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      this.initMap();
      return;
    }

    // Create script tag with loading=async (no marker library needed for classic Marker API)
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.GOOGLE_MAPS_API_KEY}&callback=initGoogleMap&loading=async`;
    script.async = true;
    script.defer = true;

    // Global callback function
    window.initGoogleMap = () => {
      this.initMap();
    };

    document.head.appendChild(script);
  }

  /**
   * Initialize Google Map
   */
  initMap() {
    const mapElement = document.getElementById("hospital-map");
    if (!mapElement) return;

    // Get responsive zoom level (zoomed out 1 level for better Vietnam view)
    const zoom =
      window.innerWidth <= 640 ? 5 : window.innerWidth <= 1180 ? 5.5 : 6;

    // Create map with custom styles and 3D buildings
    // Note: Without mapId, we cannot selectively grey out other countries.
    // The map will show all countries in full color.
    this.map = new google.maps.Map(mapElement, {
      center: this.vietnamCenter,
      zoom: zoom,
      styles: this.getMapStyles(),
      mapTypeId: google.maps.MapTypeId.ROADMAP, // Enable 3D buildings
      tilt: 45, // Tilt map to show 3D buildings (0-45 degrees)
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: "cooperative",
    });

    // Gather hospital data from DOM
    this.gatherHospitalData();

    // Initialize all markers
    this.initializeMarkers();

    // Setup event listeners for filters
    this.setupFilterListeners();
  }

  /**
   * Get custom map styles (keep Vietnam colorful and detailed)
   * Note: Without mapId, we can't selectively grey out other countries.
   * Map allows panning to all countries.
   */
  getMapStyles() {
    return [
      // Keep natural terrain colors (altitude-based)
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ visibility: "on" }],
      },
      // Water - natural blue
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#a0c4d4" }],
      },
      {
        featureType: "water",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
      },
      // Roads - show all with labels
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "road",
        elementType: "labels.text",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [{ visibility: "on" }],
      },
      // Road types with colors
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#f4a460" }, { visibility: "on" }],
      },
      {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [{ color: "#ffffff" }, { visibility: "on" }],
      },
      {
        featureType: "road.local",
        elementType: "geometry",
        stylers: [{ color: "#f5f5f5" }, { visibility: "on" }],
      },
      // Administrative borders - visible
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#a0a0a0" }, { weight: 1 }, { visibility: "on" }],
      },
      {
        featureType: "administrative",
        elementType: "labels.text",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "administrative.country",
        elementType: "geometry.stroke",
        stylers: [{ color: "#666666" }, { weight: 2 }],
      },
      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#999999" }, { weight: 1.5 }],
      },
      // POI - show important ones only
      {
        featureType: "poi",
        elementType: "all",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "poi.medical",
        elementType: "all",
        stylers: [{ visibility: "on" }],
      },
      // Enable 3D buildings
      {
        featureType: "poi.business",
        elementType: "geometry",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "poi.business",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      // Transit - hidden
      {
        featureType: "transit",
        elementType: "all",
        stylers: [{ visibility: "off" }],
      },
      // Labels - show all
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ visibility: "on" }],
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{ visibility: "on" }, { color: "#ffffff" }],
      },
    ];
  }

  /**
   * Gather hospital data from DOM
   */
  gatherHospitalData() {
    const hospitalItems = document.querySelectorAll(".hospital-search-item");

    // console.log("🏥 Found hospital items:", hospitalItems.length);

    hospitalItems.forEach((item, index) => {
      // Get coordinates from data attributes
      const dataLat = parseFloat(item.getAttribute("data-lat"));
      const dataLng = parseFloat(item.getAttribute("data-lng"));

      // FIX: Swap lat/lng because they're reversed in the database!
      // Vietnam: Latitude ~8-23°N, Longitude ~102-110°E
      // If data-lat > 100, it's actually longitude, so swap them
      let latitude, longitude;

      if (dataLat > 100) {
        // Coordinates are swapped in the database
        latitude = dataLng; // data-lng is actually latitude
        longitude = dataLat; // data-lat is actually longitude
        console.warn(`🔄 Swapped coordinates for hospital ${index + 1}`);
      } else {
        // Coordinates are correct
        latitude = dataLat;
        longitude = dataLng;
      }

      const hospitalId = item.getAttribute("data-hospital-id");
      const hospitalName =
        item.querySelector(".hospital-name")?.textContent || "";
      const hospitalAddress =
        item.querySelector(".hospital-address")?.textContent || "";
      const hospitalPhone =
        item.querySelector(".info-item:nth-child(2) .context")?.textContent ||
        "";
      const hospitalHours =
        item.querySelector(".info-item:nth-child(3) .context")?.textContent ||
        "";
      const brandId = item.getAttribute("data-brand-id");
      const brandShortName = item.getAttribute("data-brand-shortname") || "hm";

      // console.log(`Hospital ${index + 1}:`, {
      //   name: hospitalName,
      //   lat: latitude,
      //   lng: longitude,
      //   brandShortName: brandShortName,
      // });

      // Validate coordinates
      if (
        !isNaN(latitude) &&
        !isNaN(longitude) &&
        latitude !== 0 &&
        longitude !== 0
      ) {
        this.hospitals.push({
          id: hospitalId,
          name: hospitalName,
          address: hospitalAddress,
          phone: hospitalPhone,
          hours: hospitalHours,
          latitude: latitude,
          longitude: longitude,
          brandId: brandId,
          brandShortName: brandShortName,
          element: item,
        });
      } else {
        // console.warn(`⚠️ Invalid coordinates for hospital: ${hospitalName}`);
      }
    });

    // console.log("✅ Valid hospitals loaded:", this.hospitals.length);
  }

  /**
   * Initialize all hospital markers
   */
  initializeMarkers() {
    // console.log("📍 Creating markers for", this.hospitals.length, "hospitals");

    this.hospitals.forEach((hospital, index) => {
      this.createMarker(hospital);
      // console.log(`✓ Marker ${index + 1} created for ${hospital.name}`);
    });

    // console.log("✅ All markers created. Total:", this.markers.length);
  }

  /**
   * Create a marker for a hospital using classic Marker API with custom SVG icon
   */
  createMarker(hospital) {
    const position = { lat: hospital.latitude, lng: hospital.longitude };

    // Create custom marker icon using actual SVG files
    const markerIcon = {
      url: `${HM_Variables.assets_url}img/icons/pin-${hospital.brandShortName}-lg-ico.svg`,
      scaledSize: new google.maps.Size(50, 50),
      anchor: new google.maps.Point(25, 50), // Anchor at bottom center
    };

    // Create classic Marker (works without mapId)
    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      icon: markerIcon,
      title: hospital.name,
      animation: google.maps.Animation.DROP,
    });

    // Store hospital data on marker
    marker.hospitalData = hospital;

    // Create info window
    const infoWindow = new google.maps.InfoWindow({
      content: this.getInfoWindowContent(hospital),
    });

    // Add click listener to marker
    marker.addListener("click", () => {
      // Close previous info window
      if (this.activeInfoWindow) {
        this.activeInfoWindow.close();
      }

      // Open new info window
      infoWindow.open(this.map, marker);
      this.activeInfoWindow = infoWindow;

      // Smooth pan to marker
      this.map.panTo(position);

      // Set active hospital item in list and scroll to it
      this.setActiveHospitalItem(hospital.element);
      this.scrollToHospitalItem(hospital.element);
    });

    // Store marker
    this.markers.push(marker);

    // Add click listener to hospital list item
    hospital.element.addEventListener("click", () => {
      this.handleHospitalItemClick(marker, infoWindow, hospital, position);
    });
  }

  /**
   * Get marker image URL based on brand
   * Uses actual SVG files from assets folder
   */
  getMarkerImageURL(brandShortName) {
    return `${HM_Variables.assets_url}img/icons/pin-${brandShortName}-lg-ico.svg`;
  }

  /**
   * Get info window HTML content
   */
  getInfoWindowContent(hospital) {
    return `
      <div class="hospital-popup ${hospital.brandShortName}-popup">
        <h4 class="hospital-popup-name ${hospital.brandShortName}-name">${hospital.name}</h4>
        <div class="hospital-popup-info">
          <p class="hospital-popup-address ${hospital.brandShortName}-address">
            ${hospital.address}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Handle hospital list item click
   */
  handleHospitalItemClick(marker, infoWindow, hospital, position) {
    // Close previous info window
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
    }

    // Open info window
    infoWindow.open(this.map, marker);
    this.activeInfoWindow = infoWindow;

    // Smooth zoom and pan to marker
    this.map.setZoom(15);
    this.map.panTo(position);

    // Add subtle bounce animation to marker
    marker.setAnimation(google.maps.Animation.BOUNCE);
    setTimeout(() => {
      marker.setAnimation(null);
    }, 400); // Reduced duration for less pronounced bounce

    // Set active state and scroll to item
    this.setActiveHospitalItem(hospital.element);
    this.scrollToHospitalItem(hospital.element);
  }

  /**
   * Set active hospital item in list
   */
  setActiveHospitalItem(activeElement) {
    // Remove active class from all items
    document.querySelectorAll(".hospital-search-item").forEach((item) => {
      item.classList.remove("active");
    });

    // Add active class to clicked item
    activeElement.classList.add("active");
  }

  /**
   * Scroll to hospital item in the list
   */
  scrollToHospitalItem(element) {
    const $item = $(element);
    const $listContainer = $item.closest(".hospitals-search-list");

    if (!$listContainer.length) {
      return;
    }

    // Calculate scroll position
    const containerTop = $listContainer.offset().top;
    const itemTop = $item.offset().top;
    const currentScroll = $listContainer.scrollTop();
    const itemRelativeTop = itemTop - containerTop + currentScroll;

    // Scroll to item with offset (leave some space at top)
    const scrollOffset = 20; // 20px offset from top

    $listContainer.animate(
      {
        scrollTop: itemRelativeTop - scrollOffset,
      },
      500
    ); // 500ms smooth scroll animation
  }

  /**
   * Setup filter and search listeners
   */
  setupFilterListeners() {
    // Brand filter listeners
    const brandItems = document.querySelectorAll(".brands-list .brands-item");
    brandItems.forEach((item) => {
      item.addEventListener("click", () => {
        // Remove active class from all items
        brandItems.forEach((brandItem) => {
          brandItem.classList.remove("active");
        });

        // Add active class to clicked item
        item.classList.add("active");

        // Get selected brand ID
        const selectedBrandId = item.getAttribute("data-brand-id");

        // Update filter state
        this.filterState.currentBrandId = selectedBrandId;

        // Apply filters
        this.applyFilters();
      });
    });

    // Search form listener
    const searchForm = document.getElementById("search-hospital-by-name-form");
    const searchInput = document.getElementById(
      "search-hospital-by-name-input"
    );

    if (searchForm && searchInput) {
      searchForm.addEventListener("submit", (e) => {
        e.preventDefault();

        // Update search keyword
        this.filterState.searchKeyword = searchInput.value;

        // Apply filters
        this.applyFilters();
      });

      // Real-time search on input
      searchInput.addEventListener("input", () => {
        this.filterState.searchKeyword = searchInput.value;
        this.applyFilters();
      });
    }
  }

  /**
   * Apply filters to hospitals and markers
   */
  applyFilters() {
    const searchTerm = this.filterState.searchKeyword.toLowerCase();
    const brandFilter = this.filterState.currentBrandId;

    // Filter markers and list items
    this.markers.forEach((marker, index) => {
      const hospital = marker.hospitalData;
      const hospitalItem = hospital.element;

      // Normalize text for Vietnamese search
      const normalizedName = hospital.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      const normalizedSearch = searchTerm
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

      // Check filters
      const matchesBrand =
        brandFilter === "all" || hospital.brandId === brandFilter;
      const matchesSearch = normalizedName.includes(normalizedSearch);
      const isVisible = matchesBrand && matchesSearch;

      // Show/hide marker (classic Marker API)
      marker.setVisible(isVisible);

      // Show/hide list item
      if (isVisible) {
        hospitalItem.classList.remove("hide");
      } else {
        hospitalItem.classList.add("hide");
      }
    });

    // Adjust map view to show visible markers
    this.fitMapToVisibleMarkers();
  }

  /**
   * Fit map bounds to show all visible markers
   */
  fitMapToVisibleMarkers() {
    const bounds = new google.maps.LatLngBounds();
    let hasVisibleMarkers = false;

    this.markers.forEach((marker) => {
      // Check if marker is visible (classic Marker API)
      if (marker.getVisible()) {
        bounds.extend(marker.getPosition());
        hasVisibleMarkers = true;
      }
    });

    if (hasVisibleMarkers) {
      this.map.fitBounds(bounds);

      // Limit maximum zoom level
      const listener = google.maps.event.addListener(this.map, "idle", () => {
        if (this.map.getZoom() > 16) {
          this.map.setZoom(16);
        }
        google.maps.event.removeListener(listener);
      });
    } else {
      // No visible markers, reset to Vietnam view (zoomed out 1 level)
      this.map.setCenter(this.vietnamCenter);
      this.map.setZoom(
        window.innerWidth <= 640 ? 5 : window.innerWidth <= 1180 ? 5.5 : 6
      );
    }
  }

  /**
   * Initialize contact form (keep existing functionality)
   */
  initContactForm() {
    const $contactForm = $("#the-contact-form");
    if (!$contactForm.length) return;

    // Import validation from existing implementation
    // This part remains the same as the original _Contact.js
    // console.log("Contact form initialized");
  }
}
